import React, { useState } from 'react';

const DateRangeFilter = ({
  startDate,
  endDate,
  onDateChange,
  className = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState('All time');

  const presets = [
    { label: 'Last 7 days', days: 7 },
    { label: 'Last 30 days', days: 30 },
    { label: 'Last 90 days', days: 90 },
    { label: 'Last 6 months', days: 180 },
    { label: 'Last year', days: 365 },
    { label: 'Year to date', value: 'ytd' },
    { label: 'All time', value: 'all' },
  ];

  const formatDateForInput = (date) => {
    return date.toISOString().split('T')[0];
  };

  const handlePresetClick = (preset) => {
    const now = new Date();
    let start;
    let end = now;

    if (preset.value === 'all') {
      start = undefined;
      end = now;
      onDateChange(undefined, undefined);
    } else if (preset.value === 'ytd') {
      start = new Date(now.getFullYear(), 0, 1);
      onDateChange(formatDateForInput(start), formatDateForInput(end));
    } else if (preset.days) {
      start = new Date(now.getTime() - preset.days * 24 * 60 * 60 * 1000);
      onDateChange(formatDateForInput(start), formatDateForInput(end));
    }

    setSelectedPreset(preset.label);
    setIsOpen(false);
  };

  const handleCustomDateChange = (type, value) => {
    if (type === 'start') {
      onDateChange(value, endDate);
    } else {
      onDateChange(startDate, value);
    }
    setSelectedPreset('Custom');
  };

  const getDisplayText = () => {
    if (startDate && endDate) {
      const start = new Date(startDate).toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric' 
      });
      const end = new Date(endDate).toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        year: 'numeric'
      });
      return `${start} - ${end}`;
    }
    return selectedPreset;
  };

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px 16px',
          backgroundColor: 'white',
          color: '#374151',
          border: '1px solid #D1D5DB',
          borderRadius: '8px',
          fontSize: '14px',
          fontWeight: '400',
          cursor: 'pointer',
          transition: 'background-color 0.2s',
          fontFamily: 'Inter, system-ui, sans-serif',
          minWidth: '140px'
        }}
        onMouseEnter={(e) => {
          e.target.style.backgroundColor = '#F9FAFB';
        }}
        onMouseLeave={(e) => {
          e.target.style.backgroundColor = 'white';
        }}
      >
        <svg style={{ width: '16px', height: '16px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
          <line x1="16" y1="2" x2="16" y2="6"></line>
          <line x1="8" y1="2" x2="8" y2="6"></line>
          <line x1="3" y1="10" x2="21" y2="10"></line>
        </svg>
        <span style={{ fontSize: '14px', color: '#374151' }}>{getDisplayText()}</span>
        <svg style={{ width: '16px', height: '16px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <polyline points="6,9 12,15 18,9"></polyline>
        </svg>
      </button>

      {isOpen && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          marginTop: '4px',
          width: '200px',
          backgroundColor: 'white',
          border: '1px solid #E5E7EB',
          borderRadius: '8px',
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
          zIndex: 9999
        }}>
          <div style={{ padding: '12px' }}>
            <h4 style={{ 
              fontSize: '14px', 
              fontWeight: '500', 
              color: '#111827', 
              marginBottom: '12px',
              fontFamily: 'Inter, system-ui, sans-serif'
            }}>
              Date Range
            </h4>
            
            {/* Preset Options */}
            <div style={{ marginBottom: '12px' }}>
              {presets.map((preset) => (
                <button
                  key={preset.label}
                  onClick={() => handlePresetClick(preset)}
                  style={{
                    width: '100%',
                    textAlign: 'left',
                    padding: '8px 12px',
                    fontSize: '14px',
                    borderRadius: '4px',
                    border: 'none',
                    backgroundColor: selectedPreset === preset.label ? '#EFF6FF' : 'transparent',
                    color: selectedPreset === preset.label ? '#1D4ED8' : '#374151',
                    cursor: 'pointer',
                    fontFamily: 'Inter, system-ui, sans-serif',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    if (selectedPreset !== preset.label) {
                      e.target.style.backgroundColor = '#F3F4F6';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedPreset !== preset.label) {
                      e.target.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  {preset.label}
                </button>
              ))}
            </div>

            {/* Custom Date Inputs */}
            <div style={{ 
              borderTop: '1px solid #E5E7EB', 
              paddingTop: '16px' 
            }}>
              <h5 style={{ 
                fontSize: '12px', 
                fontWeight: '500', 
                color: '#6B7280', 
                textTransform: 'uppercase',
                marginBottom: '8px',
                fontFamily: 'Inter, system-ui, sans-serif'
              }}>
                Custom Range
              </h5>
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column',
                gap: '8px' 
              }}>
                <div>
                  <label style={{ 
                    display: 'block', 
                    fontSize: '12px', 
                    color: '#6B7280', 
                    marginBottom: '4px',
                    fontFamily: 'Inter, system-ui, sans-serif'
                  }}>
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={startDate || ''}
                    onChange={(e) => handleCustomDateChange('start', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      fontSize: '14px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '4px',
                      fontFamily: 'Inter, system-ui, sans-serif',
                      outline: 'none'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#3B82F6';
                      e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#D1D5DB';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>
                <div>
                  <label style={{ 
                    display: 'block', 
                    fontSize: '12px', 
                    color: '#6B7280', 
                    marginBottom: '4px',
                    fontFamily: 'Inter, system-ui, sans-serif'
                  }}>
                    End Date
                  </label>
                  <input
                    type="date"
                    value={endDate || ''}
                    onChange={(e) => handleCustomDateChange('end', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      fontSize: '14px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '4px',
                      fontFamily: 'Inter, system-ui, sans-serif',
                      outline: 'none'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#3B82F6';
                      e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#D1D5DB';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Close Button */}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'flex-end', 
              marginTop: '16px', 
              paddingTop: '16px', 
              borderTop: '1px solid #E5E7EB' 
            }}>
              <button
                onClick={() => setIsOpen(false)}
                style={{
                  padding: '4px 12px',
                  fontSize: '14px',
                  color: '#6B7280',
                  border: 'none',
                  backgroundColor: 'transparent',
                  cursor: 'pointer',
                  fontFamily: 'Inter, system-ui, sans-serif',
                  transition: 'color 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.target.style.color = '#374151';
                }}
                onMouseLeave={(e) => {
                  e.target.style.color = '#6B7280';
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Backdrop */}
      {isOpen && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 40
          }}
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

export default DateRangeFilter; 